import React, { useId } from 'react';

import { token } from '@atlaskit/tokens';

type RovoIconProps = {
	/** Accessible label; leave empty for a decorative icon (aria-hidden). */
	label?: string;
	/** Rendered size in px. Defaults to 16 to match the small avatar footprint. */
	size?: number;
	/** Whether to retain the legacy dark outer shell around the Rovo mark. */
	showBackground?: boolean;
};

/**
 * Rovo brand mark, inlined locally instead of importing from the `@atlaskit/logo`
 * barrel (which the platform prohibited-barrel-imports ratcheting rule blocks).
 * The brand colours are intentionally hard-coded to match the Rovo logo.
 */
export const RovoIcon = ({
	label = '',
	size = 16,
	showBackground = true,
}: RovoIconProps): React.JSX.Element => {
	const clipId = useId();
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 32 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			role={label ? 'img' : 'presentation'}
			aria-label={label || undefined}
			aria-hidden={label ? undefined : true}
		>
			<g clipPath={`url(#${clipId})`}>
				{showBackground && (
					<path
						data-testid="rovo-icon-background"
						d="M14.2784 0.495822C15.391 -0.146 16.75 -0.15 17.9137 0.485033L28.6364 6.66986C29.7583 7.31666 30.4528 8.51667 30.4528 9.80941V22.1789C30.4528 23.4768 29.7626 24.6715 28.6373 25.318L17.9124 31.5051C16.791 32.151 15.3815 32.1496 14.2797 31.5144L3.56007 25.3303C2.43818 24.6835 1.7439 23.4836 1.7439 22.1908V9.82129C1.7439 8.52343 2.4341 7.32872 3.55933 6.68226L14.2784 0.495822Z"
						fill={token('color.icon')}
					/>
				)}
				<g
					data-testid="rovo-icon-colours"
					transform={showBackground ? 'translate(6 6) scale(0.625)' : undefined}
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M15.0615 20.8951L5.47045 26.4325L3.56007 25.3303C2.43818 24.6835 1.7439 23.4836 1.7439 22.1908V9.82129C1.7439 8.52343 2.4341 7.32872 3.55933 6.68226L11.6173 2.0332C11.5995 2.08338 11.5824 2.13389 11.5662 2.18472C11.4171 2.6476 11.338 3.13666 11.338 3.63654V16.0061C11.338 17.7052 12.2431 19.2725 13.7181 20.1203L15.0615 20.8951Z"
						fill="#1868DB"
					/>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M15.0615 20.8945L5.47046 26.4319L14.2797 31.5144C15.3815 32.1496 16.7374 32.1609 17.8482 31.5482C17.8683 31.5326 17.8898 31.5182 17.9124 31.5051C18.704 31.0488 19.2828 30.317 19.555 29.4716C19.6657 29.1222 19.7243 28.7529 19.7243 28.3749V23.5835L15.0615 20.8945Z"
						fill="#6A9A23"
					/>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M28.6364 6.66986L26.5817 5.48438L16.9906 11.0218L18.479 11.8801C19.9542 12.7278 20.8587 14.2949 20.8587 15.9942V28.3637C20.8587 28.8643 20.7794 29.354 20.6298 29.8174C20.6138 29.8676 20.597 29.9175 20.5793 29.967L28.6373 25.318C29.7626 24.6715 30.4528 23.4768 30.4528 22.1789V9.80941C30.4528 8.51667 29.7583 7.31666 28.6364 6.66986Z"
						fill="#AF59E1"
					/>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M16.9875 11.0216L12.4691 8.41593V3.62449C12.4691 3.24694 12.5275 2.87812 12.6381 2.52898C12.9098 1.68399 13.4877 0.95247 14.2784 0.495822L14.2808 0.494438C14.3035 0.481361 14.3251 0.466857 14.3452 0.451262C15.456 -0.161474 16.8119 -0.150218 17.9137 0.485033L26.5786 5.48421L16.9875 11.0216Z"
						fill="#FCA700"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={clipId}>
					<rect width="32" height="32" fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
};
